import React, { useState, useCallback, useEffect } from 'react'
import { Editor, EditorState, CompositeDecorator, ContentState, ContentBlock } from 'draft-js'
import 'draft-js/dist/Draft.css'

const VALUE_REGEX = /<value>/g

function findValueEntities(contentBlock: ContentBlock, callback: (start: number, end: number) => void) {
  const text = contentBlock.getText()
  let matchArr, start
  while ((matchArr = VALUE_REGEX.exec(text)) !== null) {
    start = matchArr.index
    callback(start, start + matchArr[0].length)
  }
}

export const Value: React.FC<React.HTMLAttributes<HTMLDivElement>> = props => {
  return (
    <code
      className='bg-white text-sm font-monospace border-1 border-[#EFD080] rounded-full px-1 cursor-pointer'
      {...props}
    >
      {props.children}
    </code>
  )
}

const ValueEditor: React.FC<{
  value: string
  onChange: (value: string) => void
  error: boolean
  charLimit: number
}> = ({ value, onChange, error, charLimit }) => {
  const compositeDecorator = new CompositeDecorator([
    {
      strategy: findValueEntities,
      component: Value
    }
  ])

  const showCount = () => {
    return (
      <span>
        {value?.length}/{charLimit}
      </span>
    )
  }

  const [editorState, setEditorState] = useState(() => EditorState.createEmpty(compositeDecorator))

  const getCurrentText = () => {
    const currentContent = editorState?.getCurrentContent()
    const currentContentText = currentContent?.getPlainText('')
    return currentContentText
  }

  const handleBeforeInput = () => {
    const currentContentLength = getCurrentText()?.length
    if (charLimit && currentContentLength > charLimit - 1) {
      return 'handled'
    } else {
      return 'not-handled'
    }
  }

  useEffect(() => {
    setEditorState(EditorState.createWithContent(ContentState.createFromText(value), compositeDecorator))
  }, [])

  const handleEditorChange = useCallback(
    (newEditorState: EditorState) => {
      setEditorState(newEditorState)
      const content = newEditorState.getCurrentContent().getPlainText()
      onChange(content)
    },
    [onChange]
  )

  return (
    <div
      className={`relative shadow-sm border-2 border-solid border-gray-light rounded-md p-2 text-sm hover:border-primary-dark ${
        error ? 'border-red-500' : ''
      }`}
    >
      <Editor editorState={editorState} handleBeforeInput={handleBeforeInput} onChange={handleEditorChange} />
      <div className='text-[#3a35418a]  absolute top-2 right-1 text-gray-400'>{showCount()}</div>
    </div>
  )
}

export default ValueEditor
